import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { CheckboxRow, Form, RecaptchaNote, SuccessMsg, Title } from './styled';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { Checkbox } from 'kl-b2c-ui-kit';
import { useFormContext, Controller, FieldValues } from 'react-hook-form';
// eslint-disable-next-line import/no-named-as-default
import { useSettings } from 'contexts/settings.context';
import { PrimaryButton } from 'components/button';
import { CSSObject } from 'styled-components';
import { PrivacyPolicy } from 'containers/pages';
import { InvisibleSmartCaptcha } from '@yandex/smart-captcha';
import { isSkipCaptcha } from 'utils/index';

type Policy = {
    name: string;
    label: string;
    required?: boolean;
};

type FormBaseProps = {
    policies: Policy[];
    submit: (data: FieldValues) => unknown;
    loading?: boolean;
    success: boolean | undefined;
    leftSuccess?: boolean;
    resources?: {
        submitLabel?: string;
        successMessage?: string;
        title?: string;
    };
    styles?: CSSObject;
    captchaStyles?: CSSObject;
    captchaLinkStyles?: CSSObject;
    submitButton?: React.ReactElement;
};

const FormBase = (props: PropsWithChildren<FormBaseProps>) => {
    const {
        policies,
        children,
        submit,
        loading,
        resources,
        success,
        styles,
        captchaStyles,
        captchaLinkStyles,
        submitButton,
        leftSuccess,
    } = props;
    const [doi, setDoi] = useState<string>();
    const { t } = useTranslation('translation');
    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useFormContext();
    const { settings } = useSettings();
    const [token, setToken] = useState('no-token');

    const PPRef = useRef<HTMLDivElement>(null);

    const skipCaptcha = isSkipCaptcha(settings?.captchaHideCookie);

    const onSubmit = async (data: FieldValues) => {
        const status = (await submit({
            ...data,
            captchaCode: token,
            agreement: {
                ...data.agreement,
                privacyPolicyText: PPRef?.current?.innerHTML,
            },
        })) as number;

        if (status === 202) setDoi(t('success-confirm'));
    };

    useEffect(
        () => () => {
            // This will reset form on close/or submit
            reset();
        },
        []
    );

    const getMessage = (): string =>
        doi
            ? doi
            : success === true
            ? resources?.successMessage
                ? resources.successMessage
                : t('success')
            : t('oops');

    return (
        <>
            <Form
                loading={loading}
                styles={styles}
                onSubmit={handleSubmit(onSubmit)}>
                {resources?.title && (
                    <Title data-at-selector={`form-title`}>
                        {resources?.title}
                    </Title>
                )}
                {success !== undefined ? (
                    <SuccessMsg
                        leftPosition={leftSuccess}
                        data-at-selector={`success-block`}>
                        {getMessage()}
                    </SuccessMsg>
                ) : (
                    <>
                        {children}
                        {policies.map(({ name, label, required }) => (
                            <CheckboxRow key={name}>
                                <Controller
                                    name={name}
                                    control={control}
                                    rules={{ required }}
                                    defaultValue={false}
                                    render={({
                                        field: { onChange, value },
                                    }) => {
                                        const [root, key] = name.split('.');
                                        return (
                                            <Checkbox
                                                atSelector={name}
                                                value={value}
                                                size={'large'}
                                                onChange={(value) => {
                                                    onChange(value);
                                                }}
                                                isValid={
                                                    !(
                                                        errors &&
                                                        errors[root] &&
                                                        // @ts-ignore
                                                        errors[root][key]
                                                    )
                                                }>
                                                <span
                                                    data-at-selector={`${name}-label`}
                                                    dangerouslySetInnerHTML={{
                                                        __html: DOMPurify.sanitize(
                                                            label,
                                                            {
                                                                ADD_ATTR: [
                                                                    'target',
                                                                ],
                                                            }
                                                        ),
                                                    }}
                                                />
                                            </Checkbox>
                                        );
                                    }}
                                />
                            </CheckboxRow>
                        ))}

                        {settings && (
                            <>
                                {!skipCaptcha && (
                                    <InvisibleSmartCaptcha
                                        sitekey={settings.captchaSiteKey}
                                        onSuccess={setToken}
                                        visible
                                        hideShield
                                    />
                                )}
                                <RecaptchaNote
                                    data-at-selector={`recaptcha`}
                                    style={captchaStyles}
                                    captchaLinkStyles={captchaLinkStyles}>
                                    This site is protected by Yandex
                                    SmartCaptcha&nbsp;
                                    <a
                                        data-at-selector={`privacy-label`}
                                        target={`_blank`}
                                        rel={`noopener noreferrer`}
                                        href={
                                            process.env.REACT_APP_LOCALE ===
                                            'ru'
                                                ? 'https://yandex.ru/legal/smartcaptcha_notice/ru/'
                                                : `https://yandex.com/legal/smartcaptcha_notice/en/`
                                        }>
                                        Privacy Notice
                                    </a>
                                </RecaptchaNote>
                            </>
                        )}

                        {!submitButton ? (
                            <PrimaryButton
                                type={'submit'}
                                atSelector={'submit-form'}>
                                {resources?.submitLabel
                                    ? resources.submitLabel
                                    : t('saveBtnCaption')}
                            </PrimaryButton>
                        ) : (
                            submitButton
                        )}
                    </>
                )}
            </Form>
            <PrivacyPolicy isHidden ref={PPRef} />
        </>
    );
};

export default FormBase;
